import { useState, useCallback, useEffect } from 'react';
import { plants } from '../data/plants';

const STORAGE_KEY = 'garden-planner-selected-plants';

function loadSelection(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const arr = JSON.parse(stored) as string[];
      return new Set(arr);
    }
  } catch {
    // ignore parse errors
  }
  // Default: all plants selected
  return new Set(plants.map((p) => p.name));
}

function saveSelection(selected: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...selected]));
}

export function usePlantSelection() {
  const [selected, setSelected] = useState<Set<string>>(loadSelection);

  useEffect(() => {
    saveSelection(selected);
  }, [selected]);

  const toggle = useCallback((name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(plants.map((p) => p.name)));
  }, []);

  const selectNone = useCallback(() => {
    setSelected(new Set());
  }, []);

  const selectCategory = useCallback((category: string) => {
    setSelected(new Set(plants.filter((p) => p.category === category).map((p) => p.name)));
  }, []);

  const isSelected = useCallback((name: string) => selected.has(name), [selected]);

  const selectedCount = selected.size;

  return { selected, toggle, selectAll, selectNone, selectCategory, isSelected, selectedCount };
}
